import React, { useCallback, useRef, useState } from "react";
import ToolPrepPopup from "../../../shared/Popup/ToolPrepPopup";
import ToolPrepSourceCards from "../../../shared/ToolPrepSourceCards";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import DocumentFileSourceCard from "../../shared/DocumentFileSourceCard";
import DocumentPageThumbnails from "../../shared/DocumentPageThumbnails";
import { useDocumentFileSource } from "../../shared/useDocumentFileSource";
import { downloadWatermarkedPdf } from "../../shared/bakeWatermarkedPdf";
import { PDF_WATERMARK_MAX_PAGES } from "../../shared/renderPdfFirstPage";
import WatermarkControls from "../../../watermark/Popup/WatermarkControls";
import WatermarkPreview from "../../../watermark/Popup/WatermarkPreview";
import WatermarkTextFormatToolbar from "../../../watermark/Popup/WatermarkTextFormatToolbar";
import WatermarkImageFormatToolbar from "../../../watermark/Popup/WatermarkImageFormatToolbar";
import WatermarkLeftPanelOverlays from "../../../watermark/Popup/WatermarkLeftPanelOverlays";
import { WatermarkToolbarOverlayProvider } from "../../../watermark/Popup/WatermarkToolbarOverlayContext";
import { DEFAULT_WATERMARK_SETTINGS } from "../../../watermark/Popup/watermarkSettings";
import { TOOL_PREP_PREVIEW_BG, TOOL_PREP_PREPARING_PREVIEW_LABEL } from "../../../../../utils/editorEntrySession";
import {
  DOCUMENT_PREP_STAGE_INSET,
  TOOL_PREP_STAGE_INSET,
} from "../../../lib/toolPrepImageLayout";
import "./WordWatermarkerToolPrep.css";

const HINT =
  "Edit the watermark on the main page. Use the right thumbnails to switch pages.";

function convertHint(progress) {
  if (!progress) return "Converting Word to PDF…";
  if (progress.stage === "render") return "Rendering Word pages…";
  if (progress.stage === "capture") {
    return `Capturing page ${progress.page || 1} of ${progress.total || "…"}…`;
  }
  if (progress.stage === "assemble") return "Building PDF…";
  return "Converting Word to PDF…";
}

/**
 * Watermark Word Document — full-page preview + right-rail thumbnails.
 */
const WordWatermarkerToolPrep = ({ onCancel = () => {}, onDone = null }) => {
  const scrollBodyRef = useRef(null);
  const [watermark, setWatermark] = useState(DEFAULT_WATERMARK_SETTINGS);
  const [compareMode, setCompareMode] = useState("preview");
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(null);

  const mergeWatermark = useCallback((next) => {
    setWatermark((prev) => ({ ...prev, ...next }));
  }, []);

  const {
    inputRef,
    accept,
    file,
    pdfFile,
    fileName,
    fileSizeLabel,
    previewUrl,
    pageUrls,
    pageCount,
    currentPage,
    loadingPreview,
    loadingPage,
    converting,
    convertProgress,
    error,
    hasFile,
    clear,
    openPicker,
    onInputChange,
    onDropFiles,
    ensurePageUrl,
    goToPage,
    uploadProgress,
  } = useDocumentFileSource({ acceptKind: "word", busy: exporting });

  useSyncScrollbarWidth(scrollBodyRef, [
    previewUrl,
    watermark,
    hasFile,
    pageCount,
    currentPage,
  ]);

  const handleClear = () => {
    if (exporting) return;
    clear();
    setWatermark(DEFAULT_WATERMARK_SETTINGS);
    setCompareMode("preview");
    setExportError(null);
    setExportProgress(null);
    setExportSuccess(null);
  };

  const handleDone = async () => {
    if (!pdfFile || !previewUrl || exporting) return;

    const bakeSettings = { ...watermark, enabled: true };
    setExporting(true);
    setExportError(null);
    setExportSuccess(null);
    setExportProgress({
      page: 0,
      total: Math.min(pageCount || 1, PDF_WATERMARK_MAX_PAGES),
    });

    try {
      const result = await downloadWatermarkedPdf(pdfFile, bakeSettings, {
        onProgress: setExportProgress,
      });
      setExportSuccess(result.fileName || "PDF downloaded");
      if (typeof onDone === "function") {
        onDone({
          mode: "word_watermarker",
          exported: true,
          stayOpen: true,
          fileName: result.fileName,
          pageCount: result.pageCount,
        });
      }
    } catch (err) {
      setExportError(err?.message || "Could not export watermarked PDF.");
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const progressHint = converting
    ? convertHint(convertProgress)
    : exporting && exportProgress
      ? `Baking page ${exportProgress.page || 1} of ${exportProgress.total}…`
      : exportError
        ? exportError
        : exportSuccess
          ? `Downloaded ${exportSuccess}. You can adjust and download again or try new document.`
          : pageCount > PDF_WATERMARK_MAX_PAGES
            ? `${HINT} First ${PDF_WATERMARK_MAX_PAGES} pages only.`
            : HINT;

  const toolbarDisabled =
    !previewUrl || loadingPreview || converting || exporting;

  const leftContent = (
    <div className="wwp-panel">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="wwp-file-input"
        onChange={onInputChange}
      />
      <div className="wwp-sticky-top">
        <DocumentFileSourceCard
          fileName={fileName}
          fileSizeLabel={fileSizeLabel}
          pageCount={pageCount}
          acceptLabel="Word"
          kind="word"
          hint="Supported formats: DOC, DOCX"
          loading={(loadingPreview || converting) && !exporting}
          loadingLabel={
            converting ? convertHint(convertProgress) : "Loading preview…"
          }
          error={error}
          onUploadClick={openPicker}
          onFiles={onDropFiles}
          disabled={loadingPreview || converting || exporting}
          uploadProgress={uploadProgress}
          hideFilledMeta={Boolean(previewUrl && uploadProgress == null)}
        />
        {previewUrl && uploadProgress == null ? (
          <ToolPrepSourceCards
            compareOnly
            selectedKey={compareMode}
            onSelect={setCompareMode}
            originalImageUrl={previewUrl}
            previewImageUrl={previewUrl}
            watermark={watermark}
            cutoutPending={loadingPage}
            sourceName={fileName}
            sourceSizeLabel={fileSizeLabel}
            sourceMetaExtra={
              pageCount > 0
                ? `${pageCount} page${pageCount === 1 ? "" : "s"}`
                : null
            }
            onUploadClick={openPicker}
            onRemove={handleClear}
            actionsDisabled={loadingPreview || converting || exporting}
          />
        ) : null}
      </div>
      <div className="wwp-controls tool-prep-scroll" ref={scrollBodyRef}>
        <WatermarkControls
          settings={watermark}
          onChange={mergeWatermark}
          hideFormatControls
          disabled={toolbarDisabled}
          showEmbedToggle={false}
          colorPickerAnchorSelector='.tool-prep-popup[data-tool="word_watermarker"] .tool-prep-popup__left'
          presetMenuAnchorSelector='.tool-prep-popup[data-tool="word_watermarker"] .tool-prep-popup__left'
          presetMenuAnchorTopSelector='.tool-prep-popup[data-tool="word_watermarker"] .tool-prep-popup__left'
        />
      </div>
    </div>
  );

  return (
    <WatermarkToolbarOverlayProvider leftPanelSelector='.tool-prep-popup[data-tool="word_watermarker"] .tool-prep-popup__left'>
      <ToolPrepPopup
        toolKey="word_watermarker"
        title="Watermark Word Document"
        left={leftContent}
        bodyOwnsScroll
        busy={exporting || converting}
        hasSource={Boolean(
          hasFile || loadingPreview || converting || previewUrl
        )}
        preview={
          <div className="wwp-preview">
            <div className="wwp-preview__main wwp-preview-shell">
              <WatermarkTextFormatToolbar
                watermark={watermark}
                onChange={mergeWatermark}
                visible={Boolean(previewUrl)}
                disabled={toolbarDisabled}
                anchorSelector='.tool-prep-popup[data-tool="word_watermarker"] .tool-prep-popup__right'
              />
              <WatermarkImageFormatToolbar
                watermark={watermark}
                onChange={mergeWatermark}
                visible={Boolean(previewUrl)}
                disabled={toolbarDisabled}
                toolKey="word_watermarker"
              />
              <WatermarkPreview
                imageUrl={previewUrl}
                loading={loadingPreview || converting || loadingPage}
                loadingLabel={
                  converting
                    ? convertHint(convertProgress)
                    : loadingPage
                      ? `Loading page ${currentPage}…`
                      : "Loading preview…"
                }
                previewBg={TOOL_PREP_PREVIEW_BG.ORIGINAL}
                watermark={watermark}
                onWatermarkChange={mergeWatermark}
                compareMode={compareMode}
                emptyLabel={
                  uploadProgress != null && !previewUrl
                    ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                    : undefined
                }
                stageInset={
                  previewUrl
                    ? DOCUMENT_PREP_STAGE_INSET
                    : TOOL_PREP_STAGE_INSET
                }
              />
            </div>
            <DocumentPageThumbnails
              pageCount={pageCount}
              pageUrls={pageUrls}
              currentPage={currentPage}
              onSelect={goToPage}
              ensurePageUrl={ensurePageUrl}
              disabled={converting || exporting}
              maxPages={PDF_WATERMARK_MAX_PAGES}
              watermark={watermark}
              showMarks={compareMode !== "original"}
            />
          </div>
        }
        onDone={handleDone}
        onCancel={onCancel}
        confirmClose={Boolean(hasFile)}
        doneDisabled={
          !hasFile || loadingPreview || converting || !previewUrl || !pdfFile
        }
        doneLabel="Download PDF"
        onSecondaryAction={exportSuccess ? onCancel : null}
        secondaryActionLabel="Back to Dashboard"
        footerHint={progressHint}
        footerHintClassName={
          exportError
            ? "wwp-footer-error"
            : exportSuccess
              ? "wwp-footer-success"
              : ""
        }
      />
      <WatermarkLeftPanelOverlays
        settings={watermark}
        onChange={mergeWatermark}
        disabled={toolbarDisabled}
        positionStageSelector=".transform-preview__canvas"
      />
    </WatermarkToolbarOverlayProvider>
  );
};

export default WordWatermarkerToolPrep;
